import * as React from "react";
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";
import {none, option, Option} from "ts-option";
import {Icon} from "../../hiberUiKit/src/atoms/Icon/Icon";
import DataGrid from "../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import {api} from "../../api/api";
import Toast from "../helper/Toast";
import {PartnerBrandSearch} from "../../model/partnerBrand/PartnerBrand";
import DateUtils from "../../utils/DateUtils";
import {CommonDetailsStatusRow} from "./common/CommonDetailsStatusRow";
import {default as CommonDetailsInfoRow, DetailsInfoRowItem} from "./common/CommonDetailsInfoRow";
import JournalEntry from "../../model/journal/JournalEntry";
import CurrencyHelper from "../../helper/CurrencyHelper";
import JournalEntryItem from "../../model/journal/JournalEntryItem";
import Account from "../../model/account/Account";
import {notifyBugsnagAndShowToast} from "../../utils/Utils";
import PartnerBrandSelectionComponent from "../partnerBrand/search/PartnerBrandSelectionComponent";
import "./journalView.scss";

const arrow = require('../../../resources/hiber/img/guide-arrow.png');

interface State {
	/**
	 * Journal entries list.
	 */
	journalEntries: Option<JournalEntry[]>;
	/**
	 * List of accounts.
	 */
	accounts: Option<Account[]>;
	/**
	 * Selected partner brand.
	 */
	selectedPartnerBrand: Option<PartnerBrandSearch>;
}

type Props = RouteComponentProps<{}> ;

/**
 * Journal view.
 */
class JournalView extends React.PureComponent <Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {journalEntries: none, accounts: none, selectedPartnerBrand: none}
	}

	fetchData = (): void => {
		this.state.selectedPartnerBrand.map(pb => {
			Promise.all<JournalEntry[], Account[]>([
				api.instance.journalApi.fetchJournalEntries(pb.id).catch(error => {
					Toast.error("Cannot fetch journal", "Journal");
					throw error;
				}),
				api.instance.accountApi.fetchAccounts(pb.id).catch(error => {
					Toast.error("Cannot fetch accounts", "Accounts");
					throw error;
				})
			]).then(results => this.setState({journalEntries: option(results[0]), accounts: option(results[1])})
			).catch(error => {
				notifyBugsnagAndShowToast(error, "Fetch journal data");
				this.props.history.goBack();
			});
		});
	};

	render() {
		return (
			<div className={"l-page"}>
				<CommonDetailsStatusRow icon={Icon.Quote}
										title={"Journal"}
										onBackClick={this.props.history.goBack}
										refNumPath={["Accounting", "Journal"]}
										additionalComponent={this.renderPartnerBrandSelector()}/>
				{this.state.selectedPartnerBrand.map(pb => {
					const journalEntries = this.state.journalEntries.getOrElse([]);
					if (!journalEntries.length) {
						return this.renderNoDataContent();
					}
					return journalEntries.map(entry => this.renderEntry(entry));
				}).getOrElse(this.renderNoPbSelectedContent)}
			</div>
		);
	}

	renderEntry(journalEntry: JournalEntry) {
		return <div className="c-journal_section">
			{this.renderDetailsInfoRow(journalEntry)}
			{this.renderTable(journalEntry)}
		</div>
	}

	renderDetailsInfoRow = (journalEntry: JournalEntry) => {
		const detailsInfoRowItem1: DetailsInfoRowItem = {
			label: "Reference number",
			value: journalEntry.referenceNumber
		};
		const detailsInfoRowItem2: DetailsInfoRowItem = {
			label: "Description",
			value: journalEntry.description.getOrElse("")
		};
		const detailsInfoRowItem3: DetailsInfoRowItem = {
			label: "Creation Date",
			value: journalEntry.creationDateTime.format(DateUtils.DATE_TIME_FORMAT)
		};
		const detailsInfoRowItem4: DetailsInfoRowItem = {
			label: "Date",
			value: journalEntry.date.format(DateUtils.DATE_TIME_FORMAT)
		};
		return <CommonDetailsInfoRow
			items={[detailsInfoRowItem1, detailsInfoRowItem2, detailsInfoRowItem3, detailsInfoRowItem4]}/>
	};

	renderTable = (journalEntry: JournalEntry) => {
		let lastIndex = 0;
		let creditSum = 0;
		let debitSum = 0;
		return (
			<DataGrid>
				<thead>
				<tr className="c-common-details_table-header-row">
					<th className="c-common-details_table-desc-cell">ACCOUNT NAME</th>
					<th>DEBIT</th>
					<th>CREDIT</th>
				</tr>
				</thead>
				<tbody>
				{journalEntry.items.sort((a, b) => {
					const accountA = this.state.accounts.flatMap(
						accounts => option(accounts.filter(acc => acc.id === a.accountId)[0]));
					const accountB = this.state.accounts.flatMap(
						accounts => option(accounts.filter(acc => acc.id === b.accountId)[0]));
					const accountAName = accountA.map(a => a.name).getOrElse("");
					const accountBName = accountB.map(b => b.name).getOrElse("");
					return JournalEntryItem.compare(a, b, accountAName, accountBName);
				}).map((item, index) => {
						const account = this.state.accounts.flatMap(a => option(a.filter(acc => acc.id === item.accountId)[0]));
						creditSum += item.credit.getOrElse(0);
						debitSum += item.debit.getOrElse(0);
						lastIndex = index;
						return <tr key={index} className="c-common-details_table-row low-height">
							<td className="c-common-details_table-desc-cell" style={{width: "70%"}}>
								{account.map(acc => `${acc.name} (${acc.code})`).orNull}
							</td>
							<td>{item.debit.map(d => CurrencyHelper.toCurrency(d)).orNull}</td>
							<td>{item.credit.map(c => CurrencyHelper.toCurrency(c)).orNull}</td>
						</tr>
					}
				)}
				<tr key={lastIndex + 1} className="c-common-details_table-row font-bold low-height">
					<td className="c-common-details_table-desc-cell"/>
					<td>{CurrencyHelper.toCurrency(debitSum)}</td>
					<td>{CurrencyHelper.toCurrency(creditSum)}</td>
				</tr>
				</tbody>
			</DataGrid>);
	};

	renderPartnerBrandSelector = () => {
		return (
			<div className="c-common-details_partner-brand-selector">
				<PartnerBrandSelectionComponent selectedValue={this.state.selectedPartnerBrand}
												onSelect={this.onPartnerBrandChange}
												placeholder="Choose a partner brand..."/>
			</div>
		);
	};

	renderNoPbSelectedContent = () => {
		return (
			<div>
				<img src={arrow} className="c-common-details_no-pb-selected-warning_partner-brand-guide-arrow"/>
				<div className="c-common-details_no-pb-selected-warning_text">
					Please choose a partner brand to continue.
				</div>
			</div>
		);
	};

	renderNoDataContent = () => {
		return (
			<div className="c-common-details_no-pb-selected-warning_text">
				No data for selected partner brand.
			</div>
		);
	};

	onPartnerBrandChange = (selectedPartnerBrand: Option<PartnerBrandSearch>) => this.setState({selectedPartnerBrand},
		this.fetchData);
}


export default withRouter(JournalView)
